import React from 'react';
import { TrendingUp, Clock, Check } from 'lucide-react';
import { t } from 'i18next';

interface DailyRevenuePanelProps {
  completedRevenue: number;
  upcomingRevenue: number;
  totalRevenue: number;
  completedAppointments: number;
  upcomingAppointments: number;
  onClose: () => void;
}

const DailyRevenuePanel: React.FC<DailyRevenuePanelProps> = ({
  completedRevenue,
  upcomingRevenue,
  totalRevenue,
  completedAppointments,
  upcomingAppointments,
  onClose
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('dashboard.revenue', 'Recettes du jour')}</h3>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Total Revenue Card */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary-dark/20 dark:to-primary-dark/10 rounded-lg p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('dashboard.revenueTotal', 'Total des recettes')}</p>
            <p className="text-2xl font-bold text-primary dark:text-primary-light mt-1">{totalRevenue} {t('dashboard.currency', '€')}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-full p-2 shadow-sm">
            <TrendingUp size={18} className="text-primary dark:text-primary-light" />
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-2 gap-3">
        {/* Completed Revenue */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
          <div className="flex justify-between items-start mb-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('dashboard.revenueCompleted', 'Terminés')}</p>
            <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-sm">
              <Check size={14} className="text-green-500" />
            </div>
          </div>
          <p className="text-lg font-semibold text-green-700 dark:text-green-400">{completedRevenue} {t('dashboard.currency', '€')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{completedAppointments} {t('dashboard.appointments', 'rendez-vous')}</p>
        </div>

        {/* Upcoming Revenue */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
          <div className="flex justify-between items-start mb-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('dashboard.revenueUpcoming', 'À venir')}</p>
            <div className="bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-sm">
              <Clock size={14} className="text-blue-500" />
            </div>
          </div>
          <p className="text-lg font-semibold text-blue-700 dark:text-blue-400">{upcomingRevenue} {t('dashboard.currency', '€')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{upcomingAppointments} {t('dashboard.appointments', 'rendez-vous')}</p>
        </div>
      </div>

      <button
        onClick={onClose}
        className="w-full py-2 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-md transition-colors"
      >
        {t('dashboard.close', 'Close')}
      </button>
    </div>
  );
};

export default DailyRevenuePanel;
